/**
 * KillListDashboard — full Kill List grid with filters + bulk actions.
 *
 * Mirrors the v3 ROP-teams RKL queue but lives outside Teams. Bulk-select
 * lets a SysOps officer verify or execute many entries at once. Test Mode
 * entries skip the verify step server-side; we hide the bulk-verify button
 * for them client-side too.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type {
  KillListEntryDto,
  KillEntryType,
  KillStatus,
} from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { formatLocal } from '@/utils/time';

type StatusFilter = 'all' | 'pending' | 'verified' | 'executed' | 'cancelled' | 'failed';
type EntryFilter = 'all' | 'CifKill' | 'AccountKill' | 'CardRevoke' | 'NetTellerRevoke' | 'other';

const STATUS_OPTIONS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'pending', label: 'Pending verify' },
  { value: 'verified', label: 'Verified' },
  { value: 'executed', label: 'Executed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
  { value: 'all', label: 'All' },
];

const ENTRY_OPTIONS: Array<{ value: EntryFilter; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'CifKill', label: 'CIF' },
  { value: 'AccountKill', label: 'Account' },
  { value: 'CardRevoke', label: 'Card' },
  { value: 'NetTellerRevoke', label: 'NetTeller' },
  { value: 'other', label: 'Other' },
];

function toFilters(
  status: StatusFilter,
  entry: EntryFilter,
): { status?: KillStatus; entryType?: KillEntryType } {
  const out: { status?: KillStatus; entryType?: KillEntryType } = {};
  if (status === 'pending') out.status = 'pending-verification';
  else if (status === 'verified') out.status = 'verified';
  else if (status === 'executed') out.status = 'executed';
  else if (status === 'cancelled') out.status = 'cancelled';
  else if (status === 'failed') out.status = 'failed';
  if (entry !== 'all') out.entryType = entry as KillEntryType;
  return out;
}

function statusStripe(status: KillStatus): string {
  switch (status) {
    case 'executed':
      return 'stripe--ok';
    case 'cancelled':
      return 'stripe--muted';
    case 'failed':
      return 'stripe--err';
    case 'verified':
      return 'stripe--info';
    default:
      return 'stripe--warn';
  }
}

function statusPillVariant(status: KillStatus): string {
  switch (status) {
    case 'executed':
      return 'pill pill--ok';
    case 'cancelled':
      return 'pill pill--muted';
    case 'failed':
      return 'pill pill--err';
    case 'verified':
      return 'pill pill--info';
    default:
      return 'pill pill--warn';
  }
}

function entryTypeLabel(t: KillEntryType): string {
  switch (t) {
    case 'CifKill':
    case 'cif':
      return 'CIF Kill';
    case 'AccountKill':
    case 'account':
      return 'Account Kill';
    case 'CardRevoke':
    case 'card':
      return 'Card Revoke';
    case 'NetTellerRevoke':
      return 'NetTeller Revoke';
    case 'sdb-box':
      return 'SDB Box';
    case 'other':
      return 'Other';
    default:
      return String(t);
  }
}

export function KillListDashboard(): JSX.Element {
  const api = useApiClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [entryType, setEntryType] = useState<EntryFilter>('all');
  const [entries, setEntries] = useState<ReadonlyArray<KillListEntryDto>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<null | 'verify' | 'execute'>(null);
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.retail.killList.list(
        { ...toFilters(status, entryType), top: 500 },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      setEntries(resp.entries);
      setSelected(new Set());
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load kill list');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [api, status, entryType]);

  useEffect(() => {
    void refresh();
    return () => abortRef.current?.abort();
  }, [refresh]);

  const counts = useMemo(() => {
    const byStatus: Record<string, number> = {};
    for (const e of entries) byStatus[e.status] = (byStatus[e.status] ?? 0) + 1;
    return byStatus;
  }, [entries]);

  const toggleOne = useCallback((id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      if (prev.size === entries.length) return new Set();
      return new Set(entries.map((e) => e.killListEntryId));
    });
  }, [entries]);

  const runBulkVerify = useCallback(async (): Promise<void> => {
    if (!user) return;
    setBulkBusy(true);
    let failures = 0;
    for (const id of selected) {
      try {
        await api.retail.killList.verify(id, {
          verifiedByEmail: user.upn,
          approvalNote: 'Bulk-verified via SysOps Panel',
        });
      } catch {
        failures += 1;
      }
    }
    setBulkBusy(false);
    setConfirm(null);
    if (failures > 0) {
      setError(`${failures} of ${selected.size} verifies failed.`);
    }
    await refresh();
  }, [api, refresh, selected, user]);

  const runBulkExecute = useCallback(async (): Promise<void> => {
    if (!user) return;
    setBulkBusy(true);
    let failures = 0;
    let lastError = '';
    // Kill order (NetTeller → card → accounts → CIF): run the selection in that
    // order so an account is never attempted before its card in the same batch.
    const rank = new Map(entries.map((e) => [e.killListEntryId, e.killOrder ?? 5]));
    const ordered = [...selected].sort((a, b) => (rank.get(a) ?? 5) - (rank.get(b) ?? 5));
    for (const id of ordered) {
      try {
        await api.retail.killList.execute(id, { executedByEmail: user.upn });
      } catch (err) {
        failures += 1;
        lastError = err instanceof Error ? err.message : String(err);
      }
    }
    setBulkBusy(false);
    setConfirm(null);
    if (failures > 0) {
      setError(`${failures} of ${selected.size} executes failed. Last: ${lastError}`);
    }
    await refresh();
  }, [api, entries, refresh, selected, user]);

  return (
    <>
      <header className="card-header">
        <div>
          <h2>Kill List dashboard</h2>
          <p className="page-intro">
            Two-step verify → execute. Test Mode entries bypass verify. Use
            bulk select to act on many rows at once.
          </p>
        </div>
        <div className="toolbar__spacer" />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void refresh()}
        >
          Refresh
        </button>
      </header>

      <div className="toolbar" data-tour="kill-toolbar">
        <label className="toolbar__field">
          Status
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="toolbar__field">
          Entry type
          <select
            value={entryType}
            onChange={(e) => setEntryType(e.target.value as EntryFilter)}
          >
            {ENTRY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <div className="toolbar__spacer" />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={toggleAll}
          disabled={entries.length === 0}
        >
          {selected.size === entries.length && entries.length > 0
            ? 'Clear selection'
            : 'Select all'}
        </button>
      </div>

      {selected.size > 0 && (
        <div className="bulk-bar" role="region" aria-label="Bulk actions">
          <span className="bulk-bar__count">{selected.size} selected</span>
          <div className="toolbar__spacer" />
          {status === 'pending' && (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setConfirm('verify')}
              disabled={bulkBusy}
            >
              Verify selected
            </button>
          )}
          {status === 'verified' && (
            <button
              type="button"
              className="btn btn--danger btn--sm"
              onClick={() => setConfirm('execute')}
              disabled={bulkBusy}
            >
              Execute selected
            </button>
          )}
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setSelected(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      {error && (
        <div className="banner banner--err" role="alert">
          {error}
        </div>
      )}

      {loading && <p>Loading kill queue…</p>}

      {!loading && entries.length === 0 && (
        <div className="empty">No kill list entries match this filter.</div>
      )}

      {entries.length > 0 && (
        <div className="table-wrap">
          <table className="table table--striped table--hover">
            <thead>
              <tr>
                <th className="col-check">
                  <input
                    type="checkbox"
                    aria-label="Select all rows"
                    checked={
                      selected.size === entries.length && entries.length > 0
                    }
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          selected.size > 0 && selected.size < entries.length;
                    }}
                    onChange={toggleAll}
                  />
                </th>
                <th>Type</th>
                <th>Target</th>
                <th>Customer</th>
                <th>Status</th>
                <th>Reason</th>
                <th>Source</th>
                <th>Created</th>
                <th className="col-actions">Action</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => {
                const id = (e as { killListEntryId?: string; entryId?: string })
                  .killListEntryId ??
                  (e as { entryId?: string }).entryId ??
                  '';
                const checked = selected.has(id);
                // Bill 2026-07-28: the customer name is a FIRST-CLASS COLUMN on
                // the entry now (KillListEntries.CustomerName). It used to be
                // read only out of MetadataJson, which nothing ever wrote - so
                // this column rendered an em-dash for all 71 rows. Keep the
                // metadata read as a fallback for legacy rows.
                let customerName: string | null =
                  (e as { customerName?: string | null }).customerName ?? null;
                const metaJson = (e as { metadataJson?: string }).metadataJson;
                if (!customerName && metaJson) {
                  try {
                    const meta = JSON.parse(metaJson) as {
                      customerName?: string;
                    };
                    customerName = meta.customerName ?? null;
                  } catch {
                    /* ignore parse errors */
                  }
                }
                return (
                  <tr
                    key={id}
                    className={checked ? 'is-selected' : ''}
                    onClick={(ev) => {
                      const target = ev.target as HTMLElement;
                      if (
                        target.tagName !== 'INPUT' &&
                        target.tagName !== 'BUTTON'
                      )
                        navigate(`/kill-list/${encodeURIComponent(id)}`);
                    }}
                  >
                    <td className="col-check" onClick={(e2) => e2.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${id}`}
                        checked={checked}
                        onChange={() => toggleOne(id)}
                      />
                    </td>
                    <td>
                      <span className="cell-type">
                        {/* Core Director record type (CIF/DDA/SAV/CD/IRA/BOX/
                            NT/ATM), not the executor's routing discriminator.
                            entryTypeLabel is the fallback for legacy rows. */}
                        {(e as { targetType?: string | null }).targetType ??
                          entryTypeLabel(e.entryType)}
                      </span>
                    </td>
                    <td>
                      <code className="cell-id">{e.targetId}</code>
                    </td>
                    <td>{customerName ?? '—'}</td>
                    <td>
                      <span className={statusPillVariant(e.status)}>
                        {e.status}
                      </span>
                      {e.testMode && (
                        <span className="pill pill--warn pill--xs">Test</span>
                      )}
                      {e.reEntryOfEntryId && (
                        <span
                          className="pill pill--warn pill--xs"
                          title={`Already killed by an earlier entry${e.reEntryPriorExecutedAt ? ` on ${formatLocal(e.reEntryPriorExecutedAt)}` : ''}: the number was reused, or the first kill never took.`}
                        >
                          Re-entry
                        </span>
                      )}
                    </td>
                    <td>{e.reason}</td>
                    <td>
                      {e.sourceCartId ? (
                        <code className="cell-id-mute">
                          {e.sourceCartId.slice(0, 8)}…
                        </code>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="cell-time">{formatLocal(e.createdAt)}</td>
                    <td className="col-actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          navigate(`/kill-list/${encodeURIComponent(id)}`);
                        }}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {entries.length > 0 && (
        <footer className="list-footer">
          <span>Total: {entries.length}</span>
          {Object.entries(counts).map(([s, c]) => (
            <span key={s}>
              {s}: {c}
            </span>
          ))}
        </footer>
      )}

      {confirm && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal">
            <h3>
              {confirm === 'verify' ? 'Bulk verify' : 'Bulk execute'} —{' '}
              {selected.size} entries
            </h3>
            <p>
              {confirm === 'verify'
                ? 'Each selected entry will be marked verified, ready for a separate execute step.'
                : 'Each selected entry will execute the destructive action immediately. This cannot be undone.'}
            </p>
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setConfirm(null)}
                disabled={bulkBusy}
              >
                Never mind
              </button>
              <button
                type="button"
                className={
                  confirm === 'verify' ? 'btn btn--primary' : 'btn btn--danger'
                }
                onClick={() =>
                  confirm === 'verify' ? void runBulkVerify() : void runBulkExecute()
                }
                disabled={bulkBusy}
              >
                {bulkBusy ? 'Working…' : confirm === 'verify' ? 'Verify all' : 'Execute all'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
